import * as React from 'react';
import { default as moment, Moment } from 'moment-timezone';
import { styles } from './styles';

import { LeftPane } from './components/LeftPane';
import { RightPane } from './components/RightPane';
import { DateSelector } from './components/DateSelector';
import { YearSelector } from './components/YearSelector/index';

import { css } from 'emotion';
import { Modal } from '@joykit/components/Modal';
import { InjectedGroupProps } from '@joykit/components/Group';
import { ControlGroup } from '@withjoy/joykit';
import { Analytics } from '@withjoy/web-core';

export interface SubmitValue {
  month: number;
  date: number;
  year: number;
}

export interface Props extends InjectedGroupProps {
  disabled?: boolean;
  date?: Date | number;
  label?: string;
  showTime?: boolean;
  emptyStart: boolean;
  changed: boolean;
  onChange?: (submitValue: SubmitValue) => void;
  analytics: Analytics;
}

interface State {
  dateDisplay: Moment;
  show: boolean;
  showCalendar: boolean;
  showYearSelection: boolean;
  currentSelectionMoment: Moment;
}

class DatePicker extends React.Component<Props, State> {
  static defaultProps = {
    date: moment.now(),
    emptyStart: false,
    changed: true
  };

  constructor(props: Props) {
    super(props);

    const dateDisplay = this.initializeDate();
    this.state = {
      dateDisplay,
      show: false,
      showCalendar: true,
      showYearSelection: false,
      currentSelectionMoment: dateDisplay.clone()
    };
  }

  componentWillReceiveProps(nextProps: Props) {
    let dateDisplay = this.initializeDate(nextProps.date);
    if (!dateDisplay.isSame(this.state.dateDisplay)) {
      this.setState({ dateDisplay });
    }
  }

  // =========================
  // Toggle Handlers
  // =========================

  toggleModal = () => {
    if (this.props.disabled) {
      return;
    }
    this.setState({
      show: !this.state.show,
      showCalendar: true,
      showYearSelection: false,
      currentSelectionMoment: this.state.dateDisplay.clone()
    });
  };

  toggleContext = (context: 'year' | 'calendar') => {
    return () => {
      const showCalendar = context === 'calendar';
      this.setState({
        showCalendar,
        showYearSelection: !showCalendar
      });
    };
  };

  changeMonth = (delta: number) => {
    return () => {
      let nextSelectionMoment = this.state.currentSelectionMoment.clone().add(delta, 'months');
      this.setState({
        currentSelectionMoment: nextSelectionMoment
      });
    };
  };

  changeToToday = () => {
    this.props.analytics.track({ action: 'DateToday', actionType: 'select' });
    this.setState({
      currentSelectionMoment: moment(undefined)
    });
  };

  // =========================
  // Selection Handlers
  // =========================

  handleDateSelection: React.ChangeEventHandler<HTMLInputElement> = e => {
    this.props.analytics.track({ action: 'Date', actionType: 'select' });
    const target = e.target;
    if (target.nodeName === 'BUTTON' && target.dataset && target.dataset.date) {
      let nextSelectionMoment = moment(Number.parseInt(target.dataset.date, 10));
      if (nextSelectionMoment.isValid()) {
        this.setState({
          currentSelectionMoment: nextSelectionMoment
        });
      }
    }
  };

  handleYearSelection: React.ChangeEventHandler<HTMLInputElement> = e => {
    const target = e.target;
    const { currentSelectionMoment } = this.state;
    if (target.dataset.year && currentSelectionMoment.isValid()) {
      const yearSelection = Number.parseInt(target.dataset.year, 10);
      const currentSelectionMomentClone = currentSelectionMoment
        .clone()
        .add(yearSelection - currentSelectionMoment.year(), 'years');
      this.setState({
        currentSelectionMoment: currentSelectionMomentClone
      });
    }
  };

  submitDateSelection = (e: any) => {
    const { currentSelectionMoment } = this.state;
    const submitValue: SubmitValue = {
      month: currentSelectionMoment.month(),
      date: currentSelectionMoment.date(),
      year: currentSelectionMoment.year()
    };

    if (this.props.onChange) {
      this.props.onChange(submitValue);
      this.setState({ show: false });
    }
  };

  handleClose = () => {
    this.setState({
      show: false
    });
  };

  // =========================
  // Renderers
  // =========================

  buildLeftPane = () => {
    const { showCalendar, showYearSelection, currentSelectionMoment } = this.state;
    return (
      <LeftPane
        type="date"
        render={() => {
          return (
            <>
              <div className={styles.yearToggle(showYearSelection)} onClick={this.toggleContext('year')}>
                {currentSelectionMoment.format('YYYY')}
              </div>
              <div className={styles.calendarToggle(showCalendar)} onClick={this.toggleContext('calendar')}>
                {currentSelectionMoment.format('ddd')}, <br />
                {currentSelectionMoment.format('MMM DD')}
              </div>
            </>
          );
        }}
      />
    );
  };

  buildRightPane = () => {
    const { showCalendar, showYearSelection, currentSelectionMoment } = this.state;
    return (
      <RightPane
        closePortal={this.handleClose}
        handleSubmit={this.submitDateSelection}
        render={() => {
          return [
            showCalendar ? (
              <DateSelector
                key="date"
                handleMonthClick={this.changeMonth}
                handleTodayClick={this.changeToToday}
                handleDateSelection={this.handleDateSelection}
                currentSelectionMoment={currentSelectionMoment}
              />
            ) : null,

            showYearSelection ? (
              <YearSelector
                key="year"
                currentYear={currentSelectionMoment.year()}
                handleYearSelection={this.handleYearSelection}
              />
            ) : null
          ];
        }}
      />
    );
  };

  render() {
    const { label, disabled = false, inGroup = false, groupLast, emptyStart, changed } = this.props;
    const { dateDisplay } = this.state;
    const inputValue = emptyStart && !changed ? '' : dateDisplay.format('MMMM Do, YYYY');
    return (
      <>
        <div>
          <Modal
            open={this.state.show}
            size="fitToSize"
            onClose={() => {
              this.setState({ show: false });
            }}
            {...css({ padding: 0 })}
          >
            <div className={styles.container}>
              {this.buildLeftPane()}
              {this.buildRightPane()}
            </div>
          </Modal>
          <div className={styles.inputWrapper}>
            {label && <div className={styles.label}>{label}</div>}
            <ControlGroup label="When's the big day? (Leave the default date if you aren't sure)">
              <ControlGroup.Input
                label="When's the big day? (Leave the default if you aren't yet sure)"
                placeholder="Select a date if you have one!"
                className={styles.input}
                disabled={disabled}
                readOnly={true}
                value={inputValue}
                onClick={this.toggleModal}
              />
            </ControlGroup>
          </div>
        </div>
        {inGroup && !groupLast && <span className={styles.groupBreak} />}
      </>
    );
  }

  private initializeDate = (nextDate?: Date | number) => {
    const dateMoment = moment(nextDate || this.props.date);
    let dateDisplay;
    if (dateMoment.isValid()) {
      dateDisplay = dateMoment;
    } else {
      dateDisplay = moment();
    }

    return dateDisplay;
  };
}

export { DatePicker };
